import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'Dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin', 'broker'] }
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/contracts',
      name: 'Contracts',
      component: () => import('@/views/ContractsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/contracts/:id',
      name: 'Contract details',
      component: () => import('@/views/ContractDetailsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/insurance/applications',
      name: 'Insurance Applications',
      component: () => import('@/views/ApplicationsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin', 'broker'] }
    },
    {
      path: '/insurance/applications/:id',
      name: 'Insurance Application details',
      component: () => import('@/views/ApplicationDetailsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin', 'broker'] }
    },
    {
      path: '/insurance/contracts',
      name: 'Insurance Policies',
      component: () => import('@/views/ContractsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin', 'broker'] }
    },
    {
      path: '/insurance/contracts/:id',
      name: 'Insurance Policy details',
      component: () => import('@/views/ApplicationDetailsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin', 'broker'] }
    },
    {
      path: '/yacht-management/applications',
      name: 'Yacht Management Applications',
      component: () => import('@/views/ApplicationsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/yacht-management/applications/:id',
      name: 'Yacht Management Application details',
      component: () => import('@/views/ApplicationDetailsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/yacht-management/contracts',
      name: 'Yacht Management Contracts',
      component: () => import('@/views/ContractsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/yacht-management/contracts/:id',
      name: 'Yacht Management Contract details',
      component: () => import('@/views/ApplicationDetailsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/quotes',
      name: 'Quotes',
      component: () => import('@/views/QuotesView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/quotes/:id',
      name: 'Quote details',
      component: () => import('@/views/QuoteDetailsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/invoices',
      name: 'Invoices',
      component: () => import('@/views/InvoicesView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/charters',
      name: 'Charters',
      component: () => import('@/views/YachtChartersView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/charters/:id',
      name: 'Charter details',
      component: () => import('@/views/YachtCharterDetailsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    // {
    //   path: '/reports',
    //   name: 'Reports',
    //   component: () => import('@/views/ReportsView.vue'),
    //   meta: { requiresAuth: true, allowedRoles: ['admin'] }
    // },
    {
      path: '/tools/ym-services',
      name: 'YM services',
      component: () => import('@/views/YMServicesView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/tools/ym-rating',
      name: 'YM rating',
      component: () => import('@/views/YMRatingView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/tools/regional-settings',
      name: 'Regional settings',
      component: () => import('@/views/RegionalSettingsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/tools/insurance-notifications',
      name: 'Insurance notifications',
      component: () => import('@/views/InsuranceNotificationsView.vue'),
      meta: { requiresAuth: true, allowedRoles: ['admin'] }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'Page not found',
      component: () => import('@/views/PageNotFoundView.vue'),
      meta: { requiresAuth: false }
    }
  ]
})

router.afterEach((to, from, failure) => {
  if (!failure) {
    setTimeout(() => {
      window.HSStaticMethods.autoInit()
    }, 100)
  }
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const brokerInsuranceApplicationsPermission = 'application:all:insurance'
  const isInsuranceApplicationsRoute =
    to.path === '/insurance/applications' || to.path.startsWith('/insurance/applications/')
  const defaultTitle = 'Yachwisely'
  document.title = `${to.name} - ${defaultTitle}` || defaultTitle

  if (to.matched.some((record) => record.meta.requiresAuth) && !authStore.userToken) {
    // Store the intended route to redirect after login
    if (to.path !== '/login') {
      localStorage.setItem('redirectAfterLogin', to.fullPath)
    }
    next({ name: 'Login' })
  } else {
    await authStore.checkToken()
    if (authStore.userToken || to.name === 'Login') {
      // Check if route has role restrictions
      const allowedRoles = to.meta.allowedRoles
      if (allowedRoles && allowedRoles.length > 0) {
        // Fetch permissions if not already loaded
        if (authStore.userPermissions.length === 0) {
          try {
            await authStore.fetchUserPermissions()
          } catch (error) {
            console.error('Failed to fetch user permissions:', error)
            next({ name: 'Login' })
            return
          }
        }

        // Check if user has the required role
        const hasRequiredRole = allowedRoles.some((role) =>
          authStore.userPermissions.includes(`role:${role}`)
        )

        if (!hasRequiredRole) {
          // If broker tries to access admin-only route, redirect to insurance applications
          if (authStore.isBroker()) {
            next({ path: '/insurance/applications' })
          } else {
            next({ name: 'Login' })
          }
          return
        }

        // Refined broker permission model:
        // brokers may access only insurance applications routes and only with the dedicated right.
        if (authStore.isBroker()) {
          const hasInsuranceApplicationsAccess = authStore.userPermissions.includes(
            brokerInsuranceApplicationsPermission
          )

          if (!hasInsuranceApplicationsAccess) {
            next({ name: 'Login' })
            return
          }

          if (!isInsuranceApplicationsRoute) {
            next({ path: '/insurance/applications' })
            return
          }
        }
      }
      next()
    } else {
      // Store the intended route to redirect after login
      if (to.path !== '/login') {
        localStorage.setItem('redirectAfterLogin', to.fullPath)
      }
      next({ name: 'Login' })
    }
  }
})
export default router
